import { useLive } from '../store/live';
import type { FeedView } from '../lib/feed';
import { clock, ms, price } from '../lib/format';
import { FeedBadge, MarketBadge } from './Badges';

function Fact({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div>
      <div className="fact__label">{label}</div>
      <div className="fact__value num" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

export function PriceHeader({ view }: { view: FeedView }) {
  const quote = useLive((s) => s.quote);
  const provider = useLive((s) => s.provider);
  const tick = useLive((s) => s.tick);
  const symbol = provider?.symbol ?? 'XAUUSD';
  const stale = view.isStale;
  const tickIcon = tick === 'up' ? '▲' : tick === 'down' ? '▼' : '';
  const tickWord = tick === 'up' ? 'up' : tick === 'down' ? 'down' : 'unchanged';

  return (
    <section className="panel" aria-labelledby="ticker-title">
      <div className="ticker">
        <div>
          <h1 id="ticker-title" className="ticker__symbol">
            {symbol} <span className="small muted">Spot gold · USD</span>
          </h1>
          <div
            className={`ticker__price num ${stale ? 'ticker__price--stale' : ''}`}
            data-testid="mid-price"
            aria-live="off"
          >
            {quote ? price(quote.mid) : '—'}
            {quote && !stale && tickIcon ? (
              <span className={`small ${tick === 'up' ? 'pos' : 'neg'}`} style={{ marginLeft: 8, fontSize: '1rem' }}>
                <span aria-hidden="true">{tickIcon}</span>
                <span className="visually-hidden">last tick {tickWord}</span>
              </span>
            ) : null}
          </div>
          {stale && quote ? (
            <p className="small" style={{ color: 'var(--danger)' }} data-testid="stale-warning" role="status">
              ⚠ Not current — last quote at {clock(quote.sourceTimestamp)}
            </p>
          ) : null}
        </div>
        <div className="ticker__facts">
          <Fact label="Bid" value={quote ? price(quote.bid) : '—'} testId="bid" />
          <Fact label="Ask" value={quote ? price(quote.ask) : '—'} testId="ask" />
          <Fact
            label="Spread"
            value={quote ? `${price(quote.spread)} (${quote.spreadPoints.toFixed(0)} pts)` : '—'}
            testId="spread"
          />
          <Fact label="Quote time" value={clock(quote?.sourceTimestamp)} />
          <Fact label="Received" value={clock(quote?.ingestedAt)} />
          <Fact label="Feed latency" value={ms(provider?.feedLatencyMs)} />
        </div>
        <div className="ticker__badges">
          <FeedBadge view={view} />
          <MarketBadge open={view.marketOpen} />
          <span className="badge badge--neutral" data-testid="provider-name">
            Provider: {provider?.provider ?? '—'}
          </span>
        </div>
      </div>
    </section>
  );
}
